/**
 * Assessment API client (Sprint 6 B4).
 *
 * Mirror backend endpoints:
 *   - GET    /api/assessment/quizzes/?course=<slug>           — list quiz of course
 *   - POST   /api/assessment/attempts/start/                  — start attempt (idempotent)
 *   - GET    /api/assessment/attempts/<id>/                   — detail
 *   - POST   /api/assessment/attempts/<id>/answer/            — partial save
 *   - POST   /api/assessment/attempts/<id>/submit/            — finalize + auto-grade
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

// ─── Types ────────────────────────────────────────────────────────────────

export type QuestionType =
  | "multiple_choice"
  | "true_false"
  | "short_answer" // Sprint 30 — AI-assist grading, xem QuizTake.tsx
  | "essay"; // Sprint 30 — AI-assist grading, xem QuizTake.tsx

export type AttemptStatus = "in_progress" | "submitted" | "expired" | "invalidated";

export interface QuizListItem {
  id: number;
  title: string;
  description: string;
  time_limit_minutes: number;
  duration_display: string;
  max_attempts: number;
  pass_threshold_pct: number;
  webcam_required: boolean;
  total_points: number;
  created_at: string;
}

export interface QuestionChoice {
  id: number;
  text: string;
  order: number;
  /** Chỉ có khi attempt SUBMITTED (review schema). */
  is_correct?: boolean;
}

export interface Question {
  id: number;
  type: QuestionType;
  prompt: string;
  points: number;
  order: number;
  choices: QuestionChoice[];
}

export interface QuizBrief {
  id: number;
  title: string;
  time_limit_minutes: number;
  duration_display: string;
  pass_threshold_pct: number;
  webcam_required: boolean;
}

export interface AnswerSnapshot {
  selected_choice_ids: number[];
  /** Câu trả lời text cho short_answer/essay — rỗng cho MC/TF. */
  text_answer: string;
  is_correct: boolean | null;
  points_awarded: number;
  /** Nhận xét cuối instructor chấm (short_answer/essay). Rỗng nếu chưa chấm. */
  feedback: string;
  /** True sau khi instructor chốt điểm (short_answer/essay). */
  manually_graded: boolean;
}

export interface AttemptDetail {
  id: number;
  status: AttemptStatus;
  score: number;
  total_points: number;
  passed: boolean;
  /** True nếu còn câu short_answer/essay chưa được instructor chấm — `score`
   * hiện tại chưa tính các câu đó, hiện "Đang chờ chấm" thay vì điểm cuối. */
  needs_manual_grading: boolean;
  started_at: string;
  submitted_at: string | null;
  expired_at: string | null;
  deadline_at: string | null;
  quiz: QuizBrief;
  questions: Question[];
  /** Map question_id (string key vì JSON) → AnswerSnapshot. */
  answers: Record<string, AnswerSnapshot>;
  /** Sprint 6 B5/B6 — proctoring layer. Null nếu attempt cũ trước feature ship. */
  proctor_session_id: number | null;
  consent_given_at: string | null;
}

// ─── Hooks ────────────────────────────────────────────────────────────────

/**
 * GET /api/assessment/quizzes/?course=<slug>
 *
 * Chỉ trả published quiz của course đã enroll. Chưa enroll → []
 * (không 403 để FE không lộ "có quiz nhưng cấm xem").
 */
export function useQuizzes(courseSlug: string | undefined) {
  return useQuery({
    queryKey: ["assessment", "quizzes", courseSlug],
    queryFn: async () => {
      if (!courseSlug) return [];
      const resp = await apiClient.get<QuizListItem[]>("/assessment/quizzes/", {
        params: { course: courseSlug },
      });
      return resp.data;
    },
    enabled: !!courseSlug,
    staleTime: 60_000,
  });
}

/**
 * POST /api/assessment/attempts/start/  body: {quiz_id}
 *
 * Idempotent: đã có IN_PROGRESS → 200 return cũ; mới → 201; vượt max_attempts → 409.
 */
export function useStartAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quizId: number) => {
      const resp = await apiClient.post<AttemptDetail>("/assessment/attempts/start/", {
        quiz_id: quizId,
      });
      return resp.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["assessment", "attempt", data.id] });
    },
  });
}

/**
 * GET /api/assessment/attempts/<id>/
 *
 * IN_PROGRESS → questions không lộ is_correct.
 * SUBMITTED → questions có is_correct cho review screen.
 */
export function useAttempt(attemptId: number | undefined) {
  return useQuery({
    queryKey: ["assessment", "attempt", attemptId],
    queryFn: async () => {
      if (!attemptId) throw new Error("attemptId required");
      const resp = await apiClient.get<AttemptDetail>(`/assessment/attempts/${attemptId}/`);
      return resp.data;
    },
    enabled: !!attemptId,
    staleTime: 5_000,
  });
}

/**
 * POST /api/assessment/attempts/<id>/answer/
 *
 * Partial save mỗi lần user chọn đáp án. Idempotent — update record cũ.
 * KHÔNG invalidate cache ngay (sẽ flash UI) — caller dispatch refetch
 * khi cần (vd: trước submit để đảm bảo save xong).
 */
export function useAnswerQuestion(attemptId: number | undefined) {
  return useMutation({
    mutationFn: async (payload: {
      question_id: number;
      selected_choice_ids?: number[];
      /** Cho short_answer/essay — bỏ qua selected_choice_ids khi dùng field này. */
      text_answer?: string;
    }) => {
      if (!attemptId) throw new Error("attemptId required");
      const resp = await apiClient.post(`/assessment/attempts/${attemptId}/answer/`, payload);
      return resp.data as { detail: string; answer_id: number };
    },
  });
}

/**
 * POST /api/assessment/attempts/<id>/submit/
 *
 * Finalize → auto-grade MC/TF → return updated AttemptDetail (status=SUBMITTED).
 */
export function useSubmitAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attemptId: number) => {
      const resp = await apiClient.post<AttemptDetail>(`/assessment/attempts/${attemptId}/submit/`);
      return resp.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["assessment", "attempt", data.id] });
    },
  });
}

// ─── Utility ──────────────────────────────────────────────────────────────

/**
 * Tính phần trăm điểm cho FE display. `total_points=0` → 0%.
 */
export function scorePercent(attempt: Pick<AttemptDetail, "score" | "total_points">): number {
  if (attempt.total_points <= 0) return 0;
  return Math.round((attempt.score / attempt.total_points) * 100);
}

// ─── My term dashboard (dashboard sinh viên theo kỳ học) ───────────────────

export interface MyTermDashboardTerm {
  id: number;
  name: string;
  is_current: boolean;
  /** `ends_at < hôm nay` — kỳ đã kết thúc thì `is_pass` các môn mới có giá trị. */
  is_past: boolean;
}

export interface MyTermDashboardCourse {
  id: number;
  slug: string;
  code: string;
  name: string;
  quiz_count: number;
  /** % (0-100) điểm CỦA CHÍNH BẠN — dùng `pctTo10()` khi hiển thị. `null` =
   *  môn chưa có điểm nào (KHÁC 0 — 0 nghĩa là đã chấm nhưng bị điểm kém). */
  total_pct: number | null;
  /** `null` khi kỳ đang diễn ra (chưa đủ dữ liệu để kết luận đậu/rớt) hoặc
   *  chưa có điểm — chỉ có giá trị boolean ở kỳ ĐÃ QUA + đã có điểm. */
  is_pass: boolean | null;
}

export interface MyTermDashboardResponse {
  term: MyTermDashboardTerm | null;
  /** Toàn bộ kỳ bạn từng enroll (không phải mọi kỳ của trường) — dùng cho dropdown chọn kỳ. */
  terms: MyTermDashboardTerm[];
  courses: MyTermDashboardCourse[];
}

/**
 * GET /api/assessment/me/term-dashboard/?term=<id>
 *
 * `termId` optional — không truyền thì BE tự chọn kỳ hiện tại (nếu bạn có
 * enroll ở đó) hoặc kỳ gần nhất bạn từng enroll.
 */
export function useMyTermDashboard(termId: number | undefined) {
  return useQuery({
    queryKey: ["assessment", "my-term-dashboard", termId],
    queryFn: async () => {
      const resp = await apiClient.get<MyTermDashboardResponse>(
        "/assessment/me/term-dashboard/",
        { params: termId ? { term: termId } : {} },
      );
      return resp.data;
    },
  });
}
